import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, MapPin, Route, Zap, Calendar, 
  Navigation, ChevronRight, CheckCircle, AlertTriangle 
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const CreneauxDisponibles = ({ 
  isOpen, 
  onClose, 
  prospectAddress, 
  prospectLatitude, 
  prospectLongitude,
  onSelectCreneau 
}) => {
  const [loading, setLoading] = useState(false);
  const [creneauxOptimises, setCreneauxOptimises] = useState({});
  const [selectedCreneau, setSelectedCreneau] = useState(null);
  const [allRdv, setAllRdv] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [showAllSlots, setShowAllSlots] = useState({}); // Pour chaque commercial

  useEffect(() => {
    if (isOpen && prospectLatitude && prospectLongitude) {
      calculateOptimalTimeSlots();
    }
  }, [isOpen, prospectLatitude, prospectLongitude]);

  const calculateOptimalTimeSlots = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer tous les RDV existants (même ceux non visibles au Telepro)
      const rdvResponse = await axios.get(`${API}/api/rendez-vous`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Récupérer tous les prospects pour enrichir les données
      const prospectsResponse = await axios.get(`${API}/api/prospects`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Récupérer tous les commerciaux
      const usersResponse = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const commerciauxData = usersResponse.data.filter(
        user => user.role === 'commercial' || user.role === 'regie'
      );
      setCommerciaux(commerciauxData);

      // Enrichir les RDV avec les données prospect
      const enrichedRdv = rdvResponse.data
        .filter(rdv => rdv.assigned_to) // Seulement les RDV attribués
        .map(rdv => {
          const prospect = prospectsResponse.data.find(p => p.id === rdv.prospect_id);
          const commercial = commerciauxData.find(c => c.id === rdv.assigned_to);
          return {
            ...rdv,
            prospect: prospect || null,
            commercial: commercial || null
          };
        })
        .filter(rdv => rdv.prospect?.latitude && rdv.prospect?.longitude); // Seulement géolocalisés

      setAllRdv(enrichedRdv);

      // Calculer les créneaux optimisés pour les 7 prochains jours
      await generateOptimalTimeSlots(enrichedRdv, commerciauxData);

    } catch (error) {
      console.error('Error calculating optimal time slots:', error);
      toast.error('Erreur lors du calcul des créneaux optimaux');
    } finally {
      setLoading(false);
    }
  };

  const generateOptimalTimeSlots = async (existingRdv, commerciauxData) => {
    const optimalSlots = {};
    const today = new Date();
    
    // Générer créneaux pour les 7 prochains jours
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + dayOffset);
      
      // Skip weekends (optionnel)
      if (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
        continue;
      }
      
      const dateStr = targetDate.toISOString().split('T')[0];
      optimalSlots[dateStr] = await calculateDayOptimalSlots(targetDate, existingRdv, commerciauxData);
    }
    
    setCreneauxOptimises(optimalSlots);
  };

  const calculateDayOptimalSlots = async (targetDate, existingRdv, commerciauxData) => {
    const dateStr = targetDate.toISOString().split('T')[0];
    const daySlots = [];

    // Récupérer les RDV existants pour cette date
    const existingRdvThisDay = existingRdv.filter(rdv => {
      const rdvDate = new Date(rdv.date_rdv);
      return rdvDate.toDateString() === targetDate.toDateString();
    });

    // Grouper par commercial
    const rdvByCommercial = {};
    existingRdvThisDay.forEach(rdv => {
      if (!rdvByCommercial[rdv.assigned_to]) {
        rdvByCommercial[rdv.assigned_to] = [];
      }
      rdvByCommercial[rdv.assigned_to].push(rdv);
    });

    // Pour chaque commercial, calculer les meilleurs créneaux
    for (const commercial of commerciauxData) {
      const commercialRdv = rdvByCommercial[commercial.id] || [];
      const slots = await calculateCommercialOptimalSlots(
        targetDate, 
        commercialRdv, 
        commercial
      );
      daySlots.push(...slots);
    }

    // Trier par score d'optimisation (meilleur en premier)
    return daySlots.sort((a, b) => b.score - a.score);
  };

  const calculateCommercialOptimalSlots = async (targetDate, commercialRdv, commercial) => {
    const slots = [];
    
    // Heures de travail : 8h-18h
    const workStart = 8;
    const workEnd = 18;
    const slotDuration = 1; // 1h par RDV

    // Créer une liste des heures occupées
    const occupiedTimes = commercialRdv
      .map(rdv => new Date(rdv.date_rdv).getHours())
      .sort((a, b) => a - b);

    // Générer les créneaux possibles
    for (let hour = workStart; hour < workEnd; hour++) {
      if (occupiedTimes.includes(hour)) {
        continue; // Créneau déjà pris
      }

      const slotStart = new Date(targetDate);
      slotStart.setHours(hour, 0, 0, 0);

      // Calculer le score d'optimisation pour ce créneau
      const score = await calculateSlotOptimizationScore(
        slotStart, 
        commercialRdv, 
        commercial
      );

      if (score > 0) {
        slots.push({
          datetime: slotStart,
          commercial: commercial,
          score: score,
          details: await getSlotDetails(slotStart, commercialRdv, commercial)
        });
      }
    }

    return slots;
  };

  const calculateSlotOptimizationScore = async (proposedSlot, commercialRdv, commercial) => {
    if (commercialRdv.length === 0) {
      return 100; // Score parfait si aucun autre RDV
    }

    let totalTravelTime = 0;
    let minTravelTime = Infinity;

    // Calculer le temps de trajet vers tous les RDV existants
    for (const rdv of commercialRdv) {
      const travelTime = await calculateTravelTime(
        prospectLatitude, prospectLongitude,
        rdv.prospect.latitude, rdv.prospect.longitude
      );
      
      totalTravelTime += travelTime;
      minTravelTime = Math.min(minTravelTime, travelTime);
    }

    // Score basé sur :
    // 1. Proximité des autres RDV (plus proche = meilleur score)
    // 2. Position dans la journée (créneaux groupés = meilleur)
    // 3. Temps de trajet total minimisé
    
    const proximityScore = minTravelTime < 15 ? 50 : Math.max(0, 50 - minTravelTime);
    const groupingScore = calculateGroupingScore(proposedSlot, commercialRdv);
    const efficiencyScore = Math.max(0, 50 - (totalTravelTime / commercialRdv.length));

    return proximityScore + groupingScore + efficiencyScore;
  };

  const calculateGroupingScore = (proposedSlot, commercialRdv) => {
    const proposedHour = proposedSlot.getHours();
    const existingHours = commercialRdv.map(rdv => new Date(rdv.date_rdv).getHours());
    
    // Bonus si le créneau est adjacent à d'autres RDV
    const adjacentSlots = existingHours.filter(hour => 
      Math.abs(hour - proposedHour) === 1
    ).length;
    
    return adjacentSlots * 25; // 25 points par créneau adjacent
  };

  const calculateTravelTime = async (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 30; // Valeur par défaut

    try {
      // Utiliser OpenRouteService pour le calcul précis
      const response = await axios.post(
        'https://api.openrouteservice.org/v2/matrix/driving-car',
        {
          locations: [[lon1, lat1], [lon2, lat2]],
          metrics: ["duration"],
          units: "km"
        },
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_OPENROUTE_API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const duration = response.data.durations[0][1]; // en secondes
      return Math.round(duration / 60); // convertir en minutes

    } catch (error) {
      // Fallback : calcul approximatif
      const R = 6371; // Rayon de la Terre en km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      
      // Estimation : 40km/h en moyenne
      return Math.round(distance / 40 * 60);
    }
  };

  const getSlotDetails = async (proposedSlot, commercialRdv, commercial) => {
    const details = {
      nearbyRdv: [],
      totalTravelTime: 0,
      efficiency: 'high'
    };

    // Trouver les RDV proches dans le temps
    const proposedHour = proposedSlot.getHours();
    const nearbyRdv = commercialRdv.filter(rdv => {
      const rdvHour = new Date(rdv.date_rdv).getHours();
      return Math.abs(rdvHour - proposedHour) <= 2;
    });

    details.nearbyRdv = await Promise.all(
      nearbyRdv.map(async (rdv) => {
        const travelTime = await calculateTravelTime(
          prospectLatitude, prospectLongitude,
          rdv.prospect.latitude, rdv.prospect.longitude
        );
        
        details.totalTravelTime += travelTime;
        
        return {
          ...rdv,
          travelTime: travelTime,
          travelDistance: await calculateTravelDistance(
            prospectLatitude, prospectLongitude,
            rdv.prospect.latitude, rdv.prospect.longitude
          )
        };
      })
    );

    // Déterminer l'efficacité
    if (details.totalTravelTime < 30) details.efficiency = 'high';
    else if (details.totalTravelTime < 60) details.efficiency = 'medium';
    else details.efficiency = 'low';

    return details;
  };

  const calculateTravelDistance = async (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(1);
  };

  const handleSelectCreneau = (creneau) => {
    setSelectedCreneau(creneau);
  };

  const confirmCreneau = () => {
    if (selectedCreneau && onSelectCreneau) {
      onSelectCreneau({
        date_rdv: selectedCreneau.datetime.toISOString(),
        suggested_commercial: selectedCreneau.commercial,
        optimization_details: selectedCreneau.details
      });
      onClose();
    }
  };

  const getEfficiencyColor = (efficiency) => {
    switch (efficiency) {
      case 'high': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getEfficiencyIcon = (efficiency) => {
    switch (efficiency) {
      case 'high': return CheckCircle;
      case 'medium': return Clock;
      case 'low': return AlertTriangle;
      default: return Clock;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            Créneaux Optimisés - Intelligence Artificielle
          </DialogTitle>
          <p className="text-sm text-gray-600">
            Analyse de {allRdv.length} RDV existants pour proposer les meilleurs créneaux 
            selon les temps de trajet et l'optimisation des tournées
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Calcul des créneaux optimaux...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Prospect Address Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">Nouveau RDV :</span>
                  <span className="text-sm">{prospectAddress}</span>
                </div>
              </CardContent>
            </Card>

            {/* Créneaux par jour */}
            <div className="space-y-4">
              {Object.entries(creneauxOptimises).map(([date, slots]) => (
                <Card key={date} className="border border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long'
                      })}
                      <Badge variant="outline" className="ml-2">
                        {slots.length} créneaux disponibles
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {slots.slice(0, showAllSlots[date] ? slots.length : 6).map((slot, index) => {
                        const EfficiencyIcon = getEfficiencyIcon(slot.details.efficiency);
                        return (
                          <div
                            key={index}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              selectedCreneau === slot
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleSelectCreneau(slot)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-600" />
                                <span className="font-medium">
                                  {slot.datetime.toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              <Badge className="text-xs" variant="outline">
                                Score: {Math.round(slot.score)}
                              </Badge>
                            </div>

                            <div className="text-sm space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">Commercial:</span>
                                <span>{slot.commercial.first_name} {slot.commercial.last_name}</span>
                              </div>

                              <div className={`flex items-center gap-1 px-2 py-1 rounded ${getEfficiencyColor(slot.details.efficiency)}`}>
                                <EfficiencyIcon className="w-3 h-3" />
                                <span className="text-xs font-medium">
                                  Efficacité {slot.details.efficiency === 'high' ? 'Excellente' : 
                                           slot.details.efficiency === 'medium' ? 'Bonne' : 'Faible'}
                                </span>
                              </div>

                              {slot.details.nearbyRdv.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <Route className="w-3 h-3" />
                                    <span>
                                      {slot.details.nearbyRdv.length} RDV proches, 
                                      {slot.details.totalTravelTime}min de trajet total
                                    </span>
                                  </div>
                                  
                                  {slot.details.nearbyRdv.slice(0, 2).map((nearbyRdv, i) => (
                                    <div key={i} className="flex items-center gap-1 ml-4 mt-1">
                                      <ChevronRight className="w-2 h-2" />
                                      <span>
                                        {nearbyRdv.prospect.prenom} {nearbyRdv.prospect.nom} 
                                        ({nearbyRdv.travelTime}min, {nearbyRdv.travelDistance}km)
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {slots.length > 6 && (
                      <div className="mt-3 text-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowAllSlots(prev => ({
                            ...prev,
                            [date]: !prev[date]
                          }))}
                        >
                          {showAllSlots[date] 
                            ? 'Masquer les créneaux supplémentaires'
                            : `Voir ${slots.length - 6} autres créneaux`
                          }
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                onClick={confirmCreneau}
                disabled={!selectedCreneau}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmer ce créneau
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreneauxDisponibles;